import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronRight, Shield, Clock, Eye, Sparkles, Calculator, CheckCircle, ExternalLink } from 'lucide-react';
import ConfidenceRing from './ConfidenceRing';
import PositionSizer from './PositionSizer';
import { executeInMt5 } from '../../utils/mt5DeepLink';

/**
 * SignalCard — "Decision-First" Hero Component
 * Shows single best trading signal with BUY/SELL CTA
 * Fintech-grade, clean light mode design
 */
const SignalCard = ({ signal, onViewFullSetup, onExecute }) => {
  const [sizerOpen, setSizerOpen] = useState(false);
  const [mt5Status, setMt5Status] = useState(null);  // { opened, method, clipboardCopied }

  if (!signal) {
    return (
      <div data-testid="signal-card-empty" style={{
        background: '#FFFFFF',
        borderRadius: '24px',
        padding: '40px 24px',
        border: '1px solid rgba(226, 232, 240, 0.6)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
        textAlign: 'center',
      }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <Eye size={28} color="#94A3B8" />
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#0F172A', marginBottom: '8px', fontFamily: 'Poppins, sans-serif' }}>
          Scanning Market...
        </h3>
        <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '1.5' }}>
          AI is analyzing the market for the best setup. Signals appear when confidence is high enough.
        </p>
      </div>
    );
  }

  const isBuy = signal.direction === 'BUY';
  const directionColor = isBuy ? '#10B981' : '#EF4444';
  const directionBg = isBuy ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)';
  const directionLabel = isBuy ? 'LONG' : 'SHORT';

  // Parse entry zone
  const entryZone = signal.entry_zone || {};
  const entryFrom = entryZone.from || signal.entry_price || 0;
  const entryTo = entryZone.to || entryFrom;
  const entryDisplay = entryFrom ? `$${Number(entryFrom).toFixed(2)}` : 'N/A';
  const slDisplay = signal.stop_loss ? `$${Number(signal.stop_loss).toFixed(2)}` : 'N/A';
  
  // Parse TP
  const tp = signal.take_profit || [];
  const tp1 = Array.isArray(tp) ? tp[0] : (tp.tp1 || null);
  const tpDisplay = tp1 ? `$${Number(tp1).toFixed(2)}` : 'N/A';

  const confidence = signal.confidence || 0;
  const calibratedConf = signal.calibrated_confidence;
  const probWin = signal.probability_of_win;
  const rawConf = signal.confidence_raw;
  const pair = signal.pair || 'XAU/USD';
  const timeframe = signal.timeframe || 'M15';

  // Compute SL pip distance for position sizer
  const slPips = (() => {
    if (!signal.stop_loss || !entryFrom) return 20;
    const diff = Math.abs(Number(signal.stop_loss) - Number(entryFrom));
    return Math.round(diff * 10); // gold pip = 0.10
  })();
  const tpPips = (() => {
    if (!tp1 || !entryFrom) return 40;
    const diff = Math.abs(Number(tp1) - Number(entryFrom));
    return Math.round(diff * 10);
  })();

  return (
    <div data-testid="signal-card" style={{
      background: '#FFFFFF',
      borderRadius: '24px',
      padding: '24px',
      border: '1px solid rgba(226, 232, 240, 0.6)',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
    }}>
      {/* Header: Pair + Direction Badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 data-testid="signal-pair" style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', fontFamily: 'Poppins, sans-serif', margin: 0 }}>
            {pair}
          </h2>
          <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: '500' }}>{timeframe} Timeframe</span>
        </div>
        <div data-testid="signal-direction-badge" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          borderRadius: '20px',
          background: directionBg,
          border: `1px solid ${directionColor}20`,
        }}>
          {isBuy ? <TrendingUp size={16} color={directionColor} /> : <TrendingDown size={16} color={directionColor} />}
          <span style={{ fontSize: '13px', fontWeight: '700', color: directionColor, letterSpacing: '0.05em' }}>
            {directionLabel}
          </span>
        </div>
      </div>

      {/* Confidence Ring — Center */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
        <ConfidenceRing percentage={calibratedConf != null ? calibratedConf : confidence} />
      </div>

      {/* Calibrated Confidence Badge (if available) */}
      {calibratedConf != null && (
        <div data-testid="signal-calibrated-badge" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 14px',
          marginBottom: '20px',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(139, 92, 246, 0.08))',
          border: '1px solid rgba(99, 102, 241, 0.15)',
          borderRadius: '14px',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
        }}>
          <Sparkles size={14} color="#6366F1" />
          <span style={{ color: '#6366F1', fontWeight: '700' }}>ML CALIBRATED</span>
          <span style={{ color: '#64748B' }}>
            P(win) = <strong style={{ color: '#0F172A' }}>{probWin ? (probWin * 100).toFixed(0) : calibratedConf}%</strong>
          </span>
          {rawConf != null && Math.abs(calibratedConf - rawConf) >= 5 && (
            <span style={{ color: '#94A3B8', fontSize: '11px' }}>
              (raw {rawConf}%)
            </span>
          )}
        </div>
      )}

      {/* Entry / SL / TP Grid */}
      <div data-testid="signal-levels" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '12px',
        marginBottom: '24px',
      }}>
        <div style={{ textAlign: 'center', padding: '12px 8px', background: '#F8FAFC', borderRadius: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px' }}>
            Entry
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', fontFamily: 'Inter, sans-serif' }}>
            {entryDisplay}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '12px 8px', background: 'rgba(239, 68, 68, 0.04)', borderRadius: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#EF4444', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px' }}>
            Stop Loss
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#EF4444', fontFamily: 'Inter, sans-serif' }}>
            {slDisplay}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '12px 8px', background: 'rgba(16, 185, 129, 0.04)', borderRadius: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px' }}>
            Take Profit
          </div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#10B981', fontFamily: 'Inter, sans-serif' }}>
            {tpDisplay}
          </div>
        </div>
      </div>

      {/* Execute Button */}
      <button
        data-testid="signal-execute-btn"
        onClick={async () => {
          // v11.6: Show feedback IMMEDIATELY (optimistic UI) so user sees confirmation
          // even if browser blocks mt5:// protocol scheme.
          setMt5Status({ opened: false, method: 'pending', clipboardCopied: false, instructions: 'Preparing trade details...' });
          try {
            const result = await executeInMt5(signal);
            setMt5Status(result);
          } catch (err) {
            setMt5Status({ opened: false, method: 'error', clipboardCopied: false, instructions: `Error: ${err.message}. Copy details manually.` });
          }
          setTimeout(() => setMt5Status(null), 7000);
          if (onExecute) onExecute();
        }}
        style={{
          width: '100%',
          padding: '16px',
          borderRadius: '16px',
          border: 'none',
          background: isBuy
            ? 'linear-gradient(135deg, #10B981, #059669)'
            : 'linear-gradient(135deg, #EF4444, #DC2626)',
          color: '#FFFFFF',
          fontSize: '17px',
          fontWeight: '700',
          fontFamily: 'Poppins, sans-serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: isBuy
            ? '0 8px 24px rgba(16, 185, 129, 0.3)'
            : '0 8px 24px rgba(239, 68, 68, 0.3)',
          transition: 'all 0.2s ease',
          letterSpacing: '0.03em',
        }}
      >
        {isBuy ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
        Execute {signal.direction} in MT5
        <ExternalLink size={16} style={{ opacity: 0.8 }} />
      </button>

      {/* MT5 feedback banner */}
      {mt5Status && (
        <div data-testid="mt5-feedback" style={{
          marginTop: '10px', padding: '10px 14px',
          borderRadius: '12px', fontSize: '12px',
          background: mt5Status.clipboardCopied ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 166, 35, 0.08)',
          border: `1px solid ${mt5Status.clipboardCopied ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 166, 35, 0.2)'}`,
          color: mt5Status.clipboardCopied ? '#059669' : '#B45309',
          display: 'flex', alignItems: 'flex-start', gap: '8px', fontFamily: 'Inter, sans-serif',
        }}>
          <CheckCircle size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
          <div style={{ lineHeight: '1.4' }}>
            <div style={{ fontWeight: '700' }}>
              {mt5Status.opened ? 'Opening MT5...' : 'Trade details copied'}
            </div>
            <div style={{ opacity: 0.85, marginTop: '2px' }}>
              {mt5Status.instructions}
            </div>
          </div>
        </div>
      )}

      {/* View Full Setup CTA */}
      <button
        data-testid="signal-view-full-btn"
        onClick={onViewFullSetup}
        style={{
          width: '100%',
          marginTop: '12px',
          padding: '12px',
          borderRadius: '12px',
          border: '1px solid rgba(245, 166, 35, 0.2)',
          background: 'rgba(245, 166, 35, 0.06)',
          color: '#F5A623',
          fontSize: '14px',
          fontWeight: '600',
          fontFamily: 'Inter, sans-serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'all 0.2s ease',
        }}
      >
        View Full Setup
        <ChevronRight size={16} />
      </button>

      {/* Position Sizer */}
      <button
        data-testid="signal-size-calculator-btn"
        onClick={() => setSizerOpen(!sizerOpen)}
        style={{
          width: '100%',
          marginTop: '10px',
          padding: '12px',
          borderRadius: '12px',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          background: sizerOpen ? 'rgba(99, 102, 241, 0.10)' : 'rgba(99, 102, 241, 0.04)',
          color: '#6366F1',
          fontSize: '14px',
          fontWeight: '600',
          fontFamily: 'Inter, sans-serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
        }}
      >
        <Calculator size={16} />
        {sizerOpen ? 'Hide' : 'Calculate'} Position Size
      </button>

      {sizerOpen && (
        <div style={{ marginTop: '12px' }}>
          <PositionSizer
            pair={pair}
            slPips={slPips}
            tpPips={tpPips}
          />
        </div>
      )}

      {/* Why section */}
      {signal.analysis && (
        <div style={{ marginTop: '16px', padding: '14px 16px', background: '#F8FAFC', borderRadius: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <Shield size={14} color="#10B981" />
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Why This Trade
            </span>
          </div>
          <p style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5', margin: 0 }}>
            {typeof signal.analysis === 'string' ? signal.analysis.substring(0, 150) + '...' : 'Strong technical and fundamental alignment.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SignalCard;
